import { useTeam } from 'app/frontend/base/config/context/team'
import { useGuild } from 'app/frontend/base/config/context/guild'
import { Button } from '@/components/ui/button'
import { DataTable } from '@/components/ui/data-table'
import { ColumnDef } from '@tanstack/react-table'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import { AlertCircleIcon, Trash2, Undo2 } from 'lucide-react'
import { useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import AccessLevel, {
  accessLevels,
} from 'app/frontend/types/custom/AccessLevel'
import Section, { sections } from 'app/frontend/types/custom/Section'
import RestrictionType, {
  restrictionTypes,
} from 'app/frontend/types/custom/RestrictionType'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'app/frontend/base/config/context/auth'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import UserRestrictionIdentifierField from './UserRestrictionIdentifierField'
import { EditableAccessRestriction } from './accessRestrictionSchema'
import { useGuildUsers } from 'app/frontend/base/config/queries'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'

const name = 'access_restrictions_attributes'

const AccessRestrictionTable = ({ scope }: { scope: 'owner' | 'team' }) => {
  const gameVersion = useGameVersion()
  const { team } = useTeam()
  const { guild } = useGuild()
  const { t } = useTranslation()
  const { watch, setValue } = useFormContext()
  const { append, remove } = useFieldArray({ name })
  const { accessLevel } = useAuth()
  const accessRestrictions: EditableAccessRestriction[] = watch(name)
  const { data: userReferenceOptions = [], isPending: usersPending } =
    useGuildUsers(guild?.id)
  const restrictionTypeOptions = restrictionTypes.filter(
    (type) =>
      (team || type !== 'team_rank') &&
      (gameVersion.has('blizzard_api') || type !== 'game_rank'),
  )

  const filteredRestrictions = accessRestrictions
    .map((r, idx) => ({ ...r, _originalIndex: idx }))
    .filter((r) => (scope === 'owner' ? !r.teamId : r.teamId))

  const readonly = accessLevel !== 'admin'

  const columns: ColumnDef<
    EditableAccessRestriction & { _originalIndex: number }
  >[] = useMemo(
    () => [
      {
        id: 'restriction_type',
        header: 'Grant access',
        cell: (info) => {
          return (
            <SelectField<RestrictionType>
              layout='stacked'
              name={`${name}.${info.row.original._originalIndex}.restrictionType`}
              options={restrictionTypeOptions}
              disabled={!!info.row.original._destroy || readonly}
              className={`min-w-40 ${
                info.row.original._destroy ? 'opacity-30' : ''
              }`}
              optionLabelFn={(option) => t(`settings.access.types.${option}`)}
              customLabel={''}
            />
          )
        },
      },
      {
        id: 'identifier',
        header: '',
        cell: (info) => {
          const restrictionType = info.row.original.restrictionType

          if (restrictionType === 'game_rank') {
            return (
              <SelectField
                layout='stacked'
                name={`${name}.${info.row.original._originalIndex}.gameRankThreshold`}
                options={Array.from({ length: 10 }, (_, i) => ({
                  label: t(`settings.access.ranks.${i}`),
                  value: i.toString(),
                  sortLabel: '',
                }))}
                disabled={!!info.row.original._destroy || readonly}
                transformOptions={false}
                className={`min-w-36 ${
                  info.row.original._destroy ? 'opacity-30' : ''
                }`}
                customLabel={''}
                customPlaceholder={'Select rank'}
              />
            )
          } else if (restrictionType === 'user') {
            return (
              <UserRestrictionIdentifierField
                originalIndex={info.row.original._originalIndex}
                readonly={readonly}
                destroyed={!!info.row.original._destroy}
                userReferences={userReferenceOptions}
                loading={usersPending}
              />
            )
          } else if (restrictionType === 'team_rank' && team?.teamRanks) {
            return (
              <SelectField
                layout='stacked'
                className={`min-w-36 ${
                  info.row.original._destroy ? 'opacity-30' : ''
                }`}
                name={`${name}.${info.row.original._originalIndex}.teamRankId`}
                options={team.teamRanks}
                disabled={!!info.row.original._destroy || readonly}
                customLabel={''}
                isClearable={true}
                customPlaceholder={'Any rank'}
                optionLabelFn={(rank) => (
                  <span style={{ color: rank.color ?? undefined }}>
                    {rank.name}
                  </span>
                )}
                optionValueKey='id'
                disableSorting
              />
            )
          }

          return <div className='min-w-36' />
        },
      },
      {
        id: 'section',
        header: 'Section',
        cell: (info) => {
          const availableSections = [...sections].filter((section) => {
            if (section === 'none') return false
            if (section === 'loot' && !gameVersion.has('loot')) return false
            if (section === 'audit' && !gameVersion.has('audit')) return false
            if (section === 'plans' && !gameVersion.has('raid_plans')) {
              return false
            }

            return true
          })
          return (
            <SelectField<Section>
              layout='stacked'
              name={`${name}.${info.row.original._originalIndex}.section`}
              options={availableSections}
              disabled={info.row.original.defaultRestriction || readonly}
              className={`min-w-[8.6rem] ${
                info.row.original._destroy ? 'opacity-30' : ''
              }`}
              optionLabelFn={(option) =>
                t(`settings.access.sections.${option}`)
              }
              customLabel={''}
            />
          )
        },
      },
      {
        id: 'access_level',
        header: 'Level',
        cell: (info) => {
          return (
            <SelectField<AccessLevel>
              layout='stacked'
              name={`${name}.${info.row.original._originalIndex}.accessLevel`}
              options={[...accessLevels]}
              disabled={info.row.original.defaultRestriction || readonly}
              className={info.row.original._destroy ? 'opacity-30' : ''}
              customLabel={''}
            />
          )
        },
      },
      {
        id: 'actions',
        header: '',
        size: 50,
        cell: (info) => {
          return (
            <div className='flex justify-end mt-0.5'>
              <Button
                type='button'
                size='sm'
                disabled={readonly}
                variant={
                  info.row.original._destroy ? 'secondary' : 'destructive'
                }
                onClick={() =>
                  info.row.original.id
                    ? setValue(
                        `${name}.${info.row.original._originalIndex}._destroy`,
                        !info.row.original._destroy,
                      )
                    : remove(info.row.original._originalIndex)
                }
              >
                {info.row.original._destroy ? (
                  <Undo2 size={14} />
                ) : (
                  <Trash2 size={14} />
                )}
              </Button>
            </div>
          )
        },
      },
    ],
    [
      readonly,
      remove,
      restrictionTypeOptions,
      setValue,
      t,
      team?.teamRanks,
      userReferenceOptions,
      usersPending,
    ],
  )

  return (
    <div>
      {readonly ? (
        <Alert className='my-4'>
          <AlertCircleIcon className='h-4 w-4' />
          <AlertTitle>No permissions</AlertTitle>
          <AlertDescription>
            Guild rules can only be edited by users who have admin access to all
            teams in the guild.
          </AlertDescription>
        </Alert>
      ) : null}

      <DataTable
        cellClassName='align-top px-2'
        includeHeader={filteredRestrictions && filteredRestrictions.length > 0}
        columns={columns}
        data={filteredRestrictions ?? []}
        emptyMessage={() =>
          `No ${scope === 'owner' ? 'guild' : 'team'} rules configured`
        }
      />

      {readonly ? null : (
        <div className='flex justify-end'>
          <Button
            type='button'
            size='sm'
            className='mt-2'
            variant='outline'
            onClick={() =>
              append({
                teamId: scope === 'owner' ? null : team?.id,
                section: 'base',
                accessLevel: 'member',
                restrictionType: 'anyone',
                gameRankThreshold: 9,
              })
            }
          >
            Add rule
          </Button>
        </div>
      )}
    </div>
  )
}

export default AccessRestrictionTable
